import { useEffect, useState } from "react";

import type { Rule } from "@/lib/rules/ruleTypes";

let optimisticRules: Rule[] = [];
let enforcementProjection: Rule[] | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

/** Bridge Convex subscription lag after starting a quick session. */
export function addOptimisticEnforcementRule(rule: Rule) {
  optimisticRules = [...optimisticRules.filter((entry) => entry._id !== rule._id), rule];
  notify();
}

export function clearOptimisticEnforcementRule(id: string) {
  const next = optimisticRules.filter((entry) => entry._id !== id);
  if (next.length === optimisticRules.length) return;
  optimisticRules = next;
  notify();
}

/** Drop every optimistic enforcement row (e.g. after swipe-to-unlock stop/break). */
export function clearAllOptimisticEnforcementRules() {
  if (optimisticRules.length === 0) return;
  optimisticRules = [];
  notify();
}

/** Replace merged rules until Convex subscription reflects stop/break. */
export function setEnforcementProjection(rules: Rule[] | null) {
  enforcementProjection = rules;
  notify();
}

export function clearEnforcementProjection() {
  if (enforcementProjection === null) return;
  enforcementProjection = null;
  notify();
}

/** True when server rows match the active enforcement projection. */
export function isEnforcementProjectionSynced(serverRules: Rule[], projection: Rule[]): boolean {
  const serverById = new Map(serverRules.map((rule) => [rule._id, rule]));
  for (const projected of projection) {
    const server = serverById.get(projected._id);
    if (!server) {
      if (projected.source === "quick_session") continue;
      return false;
    }
    if (server.enabled !== projected.enabled) return false;
    if (server.suppressedUntil !== projected.suppressedUntil) return false;
  }
  return true;
}

export function getEnforcementProjection(): Rule[] | null {
  return enforcementProjection;
}

function subscribeOptimisticEnforcement(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useOptimisticEnforcementRules(): Rule[] {
  const [, bump] = useState(0);
  useEffect(() => subscribeOptimisticEnforcement(() => bump((value) => value + 1)), []);
  return optimisticRules;
}

export function mergeRulesWithOptimistic(rules: Rule[]): Rule[] {
  if (enforcementProjection !== null) return enforcementProjection;
  if (optimisticRules.length === 0) return rules;
  const ids = new Set(rules.map((rule) => rule._id));
  const pending = optimisticRules.filter((rule) => !ids.has(rule._id));
  return pending.length > 0 ? [...rules, ...pending] : rules;
}

export function getOptimisticEnforcementRules(): Rule[] {
  return optimisticRules;
}
